import React, { useState, useEffect, useContext } from 'react';
import {
  Typography,
  Button,
  Row,
  Col,
  Card,
  Input,
  Space,
  Tag,
  Divider,
  Tooltip,
  Pagination,
  message,
  Spin
} from 'antd';
import { ThemeContext } from '../../context/ThemeContext';
import {
  SearchOutlined,
  ArrowRightOutlined,
  BankOutlined,
  CarOutlined,
  HomeOutlined,
  RocketOutlined,
  ShopOutlined,
  SafetyOutlined,
  ArrowUpOutlined,
  StarOutlined,
  LineChartOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  CrownOutlined,
  GlobalOutlined,
  TeamOutlined,
  BuildOutlined,
  FileTextOutlined,
  ApartmentOutlined,
  RightOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import LandingLayout from './LandingLayoutRedesign';
import ISINCard from '../shared/ISINCard';
import './IssuerCategoryRedesign.css';
import apiClient from '../../services/apiClient';

const { Title, Paragraph } = Typography;
const { Search } = Input;

const formatSubOwnership = (val) => {
  if (!val) return '—';
  const clean = val.replace(/_/g, ' ').toUpperCase();
  if (clean === 'GOVERNMENT COMPANY') return 'GOVERNMENT';
  if (clean === 'PSU INSTITUTIONAL OWNED') return 'INSTITUTIONAL';
  if (clean === 'NON PSU BANK') return 'PRIVATE BANK';
  if (clean === 'NBFC PSU') return 'PSU NBFC';
  if (clean === 'NBFC NON PSU') return 'PRIVATE NBFC';
  return clean;
};

const getOwnershipClass = (typeofSubOwnership, typeOfIssuerBasedOnOwnership) => {
  const val = (typeofSubOwnership || typeOfIssuerBasedOnOwnership || '').toUpperCase();
  
  if (
    val.includes('CENTRAL_GOVERNMENT') || 
    val.includes('CENTRAL_GOV') || 
    val === 'GOVERNMENT' || 
    val === 'SOVEREIGN'
  ) {
    return 'ownership-sovereign';
  }
  
  if (
    val.includes('STATE_GOVERNMENT') || 
    val.includes('STATE_GOV')
  ) {
    return 'ownership-state';
  }
  
  if (
    val === 'GOVERNMENT_COMPANY' || 
    val === 'GOVERNMENT COMPANY' ||
    val === 'PSU' || 
    val.includes('PSU_BANK') || 
    val.includes('NBFC_PSU') || 
    val.includes('PSU_NBFC')
  ) {
    return 'ownership-psu';
  }
  
  if (
    val.includes('INST_OWNED') || 
    val.includes('INSTITUTIONAL_OWNED') || 
    val.includes('INST_CONTROLLED') ||
    val.includes('INSTITUTIONAL') ||
    val.includes('FINANCIAL_INSTITUTION')
  ) {
    return 'ownership-institutional';
  }
  
  return 'ownership-private';
};

const getRatingBorderClass = (rating) => {
  if (!rating) return 'rating-border-unrated';
  const clean = rating.toUpperCase().replace(/\s+/g, '');
  if (clean === 'NA' || clean === 'N/A' || clean === '-' || clean === '—' || clean === 'WR' || clean === 'UNRATED' || clean === 'WD') {
    return 'rating-border-unrated';
  }
  if (clean.includes('AAA')) return 'rating-border-aaa';
  if (clean.includes('AA')) return 'rating-border-aa';
  if (clean.includes('A')) return 'rating-border-a';
  if (clean.includes('BBB')) return 'rating-border-bbb';
  return 'rating-border-sub-bbb';
};

const getRatingBadgeClass = (rating) => {
  if (!rating) return 'rating-badge-unrated';
  const clean = rating.toUpperCase().replace(/\s+/g, '');
  if (clean === 'NA' || clean === 'N/A' || clean === '-' || clean === '—' || clean === 'WR' || clean === 'UNRATED' || clean === 'WD') {
    return 'rating-badge-unrated';
  }
  if (clean.includes('AAA')) return 'rating-badge-aaa';
  if (clean.includes('AA')) return 'rating-badge-aa';
  if (clean.includes('A')) return 'rating-badge-a';
  if (clean.includes('BBB')) return 'rating-badge-bbb';
  return 'rating-badge-sub-bbb';
};



const formatMarketCap = (val) => {
  if (val == null || val === '') return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  if (num === 0) return '—';
  return '₹' + num.toLocaleString('en-IN', { maximumFractionDigits: 2 }) + ' Cr';
};

const formatBondBorrowings = (val) => {
  if (val == null || val === '') return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  if (num === 0) return '—';
  return '₹' + num.toLocaleString('en-IN', { maximumFractionDigits: 2 }) + ' Cr';
};

const getMarketCapValue = (item) => {
  if (item.marketcap && parseFloat(item.marketcap) > 0) {
    return formatMarketCap(item.marketcap);
  }
  return '—';
};

const getBondBorrowingsValue = (item) => {
  if (item.totalBondBorrowings && parseFloat(item.totalBondBorrowings) > 0) {
    return formatBondBorrowings(item.totalBondBorrowings);
  }
  return '—';
};

const formatClassificationValue = (value) => {
  if (!value || value === 'N/A') return 'N/A';
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const cleanRatingValue = (rating) => {
  if (!rating) return 'NA';
  let cleaned = rating.toString().trim();
  cleaned = cleaned.replace(/^\[[A-Za-z]+\]/i, '');
  cleaned = cleaned.replace(/^(CARE|IND|CRISIL|ICRA|FITCH|S&P|SP)\s+/i, '');
  cleaned = cleaned.replace(/\s*[\(\/;\-]\s*(stable|negative|positive|developing|rating|watch|outlook).*$/i, '');
  cleaned = cleaned.replace(/\s*\([^)]*\)/g, '');
  cleaned = cleaned.trim().toUpperCase();
  if (cleaned === '-' || cleaned === '—' || cleaned === 'WR' || cleaned === 'UNRATED' || cleaned === 'WD' || cleaned === 'N/A' || cleaned === 'NA' || !cleaned) {
    return 'NA';
  }
  return cleaned;
};

const IssuerCategoryRedesign = ({ skipLayout = false }) => {
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params or defaults
  const [searchTerm, setSearchTerm] = useState('');
  const [issuersData, setIssuersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('size')) || 16);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'government');
  const [categoryCounts, setCategoryCounts] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubcategories, setExpandedSubcategories] = useState({});
  const [highlightedMainCategory, setHighlightedMainCategory] = useState(null);

  // Background colors for issuer cards
  const backgroundColors = [
    '#F3CDCA',
    '#D9CAF2',
    '#F2C5E5',
    '#CBF2F3',
    '#CBF3D9'
  ];

  // Base URL from environment variable
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';
  const isinBaseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8083/api/v1/sb';

  // Bond category definitions (main categories - for display only)
  const bondCategories = {
    'government': "Government Securities",
    'stategovernment': "State Government Securities",
    'municipal-bonds': "Municipal Bonds"
  };

  // Hierarchical category structure (3 levels) - Using IssuerCategory enum values
  const categoryHierarchy = [
    { 
      key: 'government', 
      label: 'Central Government', 
      icon: <CrownOutlined />,
      type: 'main'
    },
    { 
      key: 'stategovernment', 
      label: 'State Government', 
      icon: <HomeOutlined />,
      type: 'main'
    },
   
    {
      key: 'psu-bonds',
      label: 'Public Sector Undertaking',
      icon: <ApartmentOutlined />,
      type: 'main',
      subcategories: [
        { key: 'PSU_CENTRAL_GOV_OWNED', label: 'Govt. of India Owned', icon: <CrownOutlined /> },
        { key: 'PSU_STATE_GOV_OWNED', label: 'Other Governments/Institutions Owned', icon: <ApartmentOutlined /> }
      ]
    },
    {
      key: 'bank-bonds',
      label: 'Commercial Banks',
      icon: <BankOutlined />,
      type: 'main',
      subcategories: [
        { key: 'BANK_PUBLIC', label: 'PSU', icon: <BankOutlined /> },
        { key: 'BANK_PRIVATE', label: 'Non-PSU', icon: <BankOutlined /> }
      ]
    },
    {
      key: 'nbfc-bonds',
      label: 'NBFC',
      icon: <BuildOutlined />,
      type: 'main',
      subcategories: [
        { 
          key: 'nbfc-psu', 
          label: 'PSU', 
          icon: <ApartmentOutlined />,
          subcategories: [
            { key: 'PSU_NBFC_CENTRAL_GOV_OWNED', label: ' Government Owned', icon: <CrownOutlined /> },
            { key: 'PSU_NBFC_STATE_GOV_OWNED', label: 'Institution Owned', icon: <ApartmentOutlined /> }
          ]
        },
        { 
          key: 'nbfc-non-psu', 
          label: 'Non-PSU', 
          icon: <BuildOutlined />,
          subcategories: [
            { key: 'NON_PSU_NBFC_PROMOTER_OWNED', label: 'Indian Promoter Owned', icon: <TeamOutlined /> },
            { key: 'NON_PSU_NBFC_PROMOTER_CONTROLLED', label: 'Indian Promoter Controlled', icon: <TeamOutlined /> },
            { key: 'NON_PSU_NBFC_FOREIGN_OWNED', label: 'Foreign Promoter Owned', icon: <GlobalOutlined /> },
            { key: 'NON_PSU_NBFC_FOREIGN_CONTROLLED', label: 'Foreign Promoter Controlled', icon: <GlobalOutlined /> },
            { key: 'NON_PSU_NBFC_WIDELY_HELD', label: 'Widely Held', icon: <TeamOutlined /> },
            { key: 'NON_PSU_NBFC_INST_OWNED', label: 'Institutional Owned', icon: <BankOutlined /> },
            { key: 'NON_PSU_NBFC_INST_CONTROLLED', label: 'Institutional Controlled', icon: <BankOutlined /> }
          ]
        }
      ]
    },
    { 
      key: 'MUNICIPAL_BONDS', 
      label: 'Municipal Corporations', 
      icon: <HomeOutlined />,
      type: 'main'
    },
    {
      key: 'non-psu-bonds',
      label: 'Private Sector',
      icon: <GlobalOutlined />,
      type: 'main',
      subcategories: [
        { key: 'NON_PSU_PROMOTER_OWNED', label: 'Indian Promoter Owned', icon: <TeamOutlined /> },
        { key: 'NON_PSU_PROMOTER_CONTROLLED', label: 'Indian Promoter Controlled', icon: <TeamOutlined /> },
        { key: 'NON_PSU_FOREIGN_OWNED', label: 'Foreign Promoter Owned', icon: <GlobalOutlined /> },
        { key: 'NON_PSU_FOREIGN_CONTROLLED', label: 'Foreign Promoter Controlled', icon: <GlobalOutlined /> },
        { key: 'NON_PSU_WIDELY_HELD', label: 'Widely Held', icon: <TeamOutlined /> },
        { key: 'NON_PSU_INST_OWNED', label: 'Institutional Owned', icon: <BankOutlined /> },
        { key: 'NON_PSU_INST_CONTROLLED', label: 'Institutional Controlled', icon: <BankOutlined /> }
      ]
    },
    {
      key: 'OTHER',
      label: 'Other',
      icon: <GlobalOutlined />,
      type: 'main'
    }
  ];

  // Helper to enrich a list of issuers with detailed data
  const enrichIssuers = async (list) => {
    if (!Array.isArray(list) || list.length === 0) return list;
    return Promise.all(
      list.map(async (item) => {
        let enrichedItem = { ...item };
        
        // 1. Fetch details from master-issuer
        if (item.corporateIdentityNumber) {
          try {
            const detailRes = await apiClient.get(`${baseUrl}/master-issuer/${item.corporateIdentityNumber}`);
            if (detailRes.data && detailRes.data.statusCode === 200 && detailRes.data.data) {
              enrichedItem = { ...enrichedItem, ...detailRes.data.data };
            }
          } catch (err) {
            console.error('Error fetching details for ' + item.corporateIdentityNumber, err);
          }
        }
        
        // 2. Fetch ratings from ratings/issuer
        const issuerName = enrichedItem.issuer || item.issuer;
        if (issuerName) {
          try {
            const ratingsRes = await apiClient.get(`${baseUrl}/ratings/issuer/${encodeURIComponent(issuerName)}`);
            if (ratingsRes.data && ratingsRes.data.statusCode === 200 && ratingsRes.data.data) {
              const ratingsData = ratingsRes.data.data;
              const agencies = ['crisil', 'icra', 'care', 'india', 'fitch', 'sp'];
              let foundRating = null;
              
              for (const agency of agencies) {
                const agencyRatings = ratingsData[agency];
                if (agencyRatings && Array.isArray(agencyRatings) && agencyRatings.length > 0) {
                  const rec = agencyRatings[0];
                  const rVal = rec.rating || (rec.id && rec.id.rating);
                  if (rVal) {
                    foundRating = rVal;
                    break;
                  }
                }
              }
              
              if (foundRating) {
                enrichedItem.rating = cleanRatingValue(foundRating);
              }
            }
          } catch (err) {
            console.error('Error fetching ratings for ' + issuerName, err);
          }
        }
        
        return enrichedItem;
      })
    );
  };

  // Fetch issuers data with pagination and category filtering
  const fetchIssuersData = async (page = currentPage, size = pageSize, category = selectedCategory) => {
    try {
      setLoading(true);
      let url;
      
      if (category === 'government' || category === 'stategovernment') {
        // For ISIN categories, use the ISIN API
        url = `${isinBaseUrl}/isin-category/category/${category}?page=${page - 1}&size=${size}`;
      } else {
        // For issuer categories using IssuerCategory enum, use the new category endpoint
        url = `${baseUrl}/master-issuer/category/${category}?page=${page - 1}&size=${size}`;
      }
      
      const response = await apiClient.get(url);
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = response.data;

      // Handle the API response structure
      // For ISIN categories: result.statusCode, for Issuer categories: result.status or result.statusCode
      if ((result.statusCode === 200 || result.status === 200) && result.data) {
        if (result.data.content && Array.isArray(result.data.content)) {
          const data = result.data.content;
          const enriched = await enrichIssuers(data);
          setIssuersData(enriched);
          setTotalItems(result.data.totalElements || data.length);
          setCurrentPage((result.data.pageNumber || 0) + 1);
          setPageSize(result.data.pageSize || size);
          console.log('Bond Categories API Response:', result);
        } else if (Array.isArray(result.data)) {
          // Handle direct array in data field
          const enriched = await enrichIssuers(result.data);
          setIssuersData(enriched);
          setTotalItems(result.data.length);
          console.log('Bond Categories API Response (array):', result);
        }
      } else if (Array.isArray(result)) {
        // Fallback for old structure
        const data = result;
        const enriched = await enrichIssuers(data);
        setIssuersData(enriched);
        setTotalItems(data.length);
        console.log('Bond Categories API Response (fallback):', result);
      } else {
        console.error('Invalid bond categories API response structure:', result);
        setIssuersData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching bond categories data:', error);
      message.error('Failed to fetch bond categories data');
      setIssuersData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch category statistics - Get counts for each category
  const fetchCategoryStatistics = async () => {
    try {
      const counts = {};
      
      // Fetch ISIN category counts (government, stategovernment)
      const isinCategories = ['government', 'stategovernment'];
      for (const category of isinCategories) {
        try {
          const response = await apiClient.get(`${isinBaseUrl}/isin-category/category/${category}?page=0&size=1`, {
            validateStatus: () => true,
          });
          if (response.status === 200) {
            const result = response.data;
            if (result.statusCode === 200 && result.data) {
              counts[category] = result.data.totalElements || 0;
            }
          }
        } catch (error) {
          console.error(`Error fetching count for ${category}:`, error);
          counts[category] = 0;
        }
      }
      
      // Fetch all issuer category counts in one API call
      try {
        const response = await apiClient.get(`${baseUrl}/master-issuer/category-counts`, {
          validateStatus: () => true,
        });
        if (response.status === 200) {
          const result = response.data;
          console.log('Category counts API response:', result);
          if ((result.status === 200 || result.statusCode === 200) && result.data) {
            // Merge issuer category counts with existing counts
            Object.assign(counts, result.data);
            console.log('Merged counts:', counts);
          }
        }
      } catch (error) {
        console.error('Error fetching issuer category counts:', error);
      }
      
      setCategoryCounts(counts);
      console.log('Category statistics:', counts);
    } catch (error) {
      console.error('Error fetching category statistics:', error);
    }
  };

  // Update URL params helper
  const updateURLParams = (params) => {
    const newSearchParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  // Handle page change
  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    updateURLParams({ page, size, category: selectedCategory });
    fetchIssuersData(page, size, selectedCategory);
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    updateURLParams({ page: 1, size: pageSize, category });
    
    // Set highlighted main category for main categories without subcategories
    const mainCategory = categoryHierarchy.find(cat => cat.key === category);
    if (mainCategory && !mainCategory.subcategories) {
      setHighlightedMainCategory(category);
    }
    
    fetchIssuersData(1, pageSize, category);
  };

  // Handle main category toggle
  const handleMainCategoryToggle = (categoryKey) => {
    // Set the main category as highlighted
    setHighlightedMainCategory(categoryKey);
    
    setExpandedCategories(prev => {
      const newState = {};
      // Close all other categories and toggle the clicked one
      categoryHierarchy.forEach(cat => {
        if (cat.key === categoryKey) {
          newState[cat.key] = !prev[cat.key];
        } else {
          newState[cat.key] = false; // Close all others
        }
      });
      return newState;
    });
  };

  // Handle Level 2 subcategory click (with potential Level 3)
  const handleSubcategoryClick = (subcategoryKey, parentKey, subcategory) => {
    // If this subcategory has its own subcategories (Level 3), just toggle expansion (don't select yet)
    if (subcategory.subcategories && subcategory.subcategories.length > 0) {
      setExpandedSubcategories(prev => ({
        ...prev,
        [subcategoryKey]: !prev[subcategoryKey]
      }));
    } else {
      // No Level 3, so select this Level 2 category directly
      setSelectedCategory(subcategoryKey);
      setCurrentPage(1);
      setHighlightedMainCategory(parentKey);
      updateURLParams({ page: 1, size: pageSize, category: subcategoryKey });
      fetchIssuersData(1, pageSize, subcategoryKey);
    }
  };
  
  // Handle hovering over Level 2 item with Level 3 subcategories
  const handleSubcategoryHover = (subcategoryKey, hasSubcategories) => {
    if (hasSubcategories) {
      // Close all other subcategories and open only this one
      setExpandedSubcategories({
        [subcategoryKey]: true
      });
    } else {
      // Close all subcategories if hovering over one without children
      setExpandedSubcategories({});
    }
  };

  // Handle Level 3 subcategory click
  const handleLevel3SubcategoryClick = (subcategoryKey, mainCategoryKey) => {
    setSelectedCategory(subcategoryKey);
    setCurrentPage(1);
    setHighlightedMainCategory(mainCategoryKey);
    updateURLParams({ page: 1, size: pageSize, category: subcategoryKey });
    fetchIssuersData(1, pageSize, subcategoryKey);
  };

  // Get the main category for a given subcategory (any level)
  const getMainCategoryForSubcategory = (subcategoryKey) => {
    for (const mainCategory of categoryHierarchy) {
      if (mainCategory.subcategories) {
        // Check Level 2
        for (const subcategory of mainCategory.subcategories) {
          if (subcategory.key === subcategoryKey) {
            return mainCategory.key;
          }
          // Check Level 3
          if (subcategory.subcategories) {
            for (const level3Sub of subcategory.subcategories) {
              if (level3Sub.key === subcategoryKey) {
                return mainCategory.key;
              }
            }
          }
        }
      }
    }
    return null;
  };

  // Check if main category should be highlighted
  const isMainCategoryHighlighted = (mainCategoryKey) => {
    // Highlight if it's the highlighted main category OR if this main category is directly selected OR if a subcategory of this main category is selected
    return highlightedMainCategory === mainCategoryKey || 
           selectedCategory === mainCategoryKey ||
           (selectedCategory !== 'government' && selectedCategory !== 'stategovernment' && selectedCategory !== 'MUNICIPAL_BONDS' && selectedCategory !== 'OTHER' && getMainCategoryForSubcategory(selectedCategory) === mainCategoryKey);
  };

  // Get breadcrumb path for selected category
  const getBreadcrumbPath = () => {
    const path = [{ label: 'Bond Categories', key: null }];
    for (const category of categoryHierarchy) {
      if (category.key === selectedCategory) {
        path.push({ label: category.label, key: category.key });
        return path;
      }
      if (category.subcategories) {
        for (const subcat of category.subcategories) {
          if (subcat.key === selectedCategory) {
            path.push({ label: category.label, key: category.key });
            path.push({ label: subcat.label, key: subcat.key });
            return path;
          }
          if (subcat.subcategories) {
            for (const level3 of subcat.subcategories) {
              if (level3.key === selectedCategory) {
                path.push({ label: category.label, key: category.key });
                path.push({ label: subcat.label, key: subcat.key });
                path.push({ label: level3.label, key: level3.key });
                return path;
              }
            }
          }
        }
      }
    }
    return path;
  };

  const totalIssuersAcrossCategories = () => {
    const keys = new Set();
    categoryHierarchy.forEach(cat => {
      if (cat.subcategories) {
        cat.subcategories.forEach(sub => {
          if (sub.subcategories) {
            sub.subcategories.forEach(sub3 => keys.add(sub3.key));
          } else {
            keys.add(sub.key);
          }
        });
      } else {
        keys.add(cat.key);
      }
    });
    return Array.from(keys).reduce((sum, key) => sum + (categoryCounts[key] || 0), 0);
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
    // Reset to first page when searching
    fetchIssuersData(1, pageSize, selectedCategory);
  };

  // Fetch data on component mount and when URL params change
  useEffect(() => {
    // Get params from URL or use defaults
    const urlCategory = searchParams.get('category') || 'government';
    const urlPage = parseInt(searchParams.get('page')) || 1;
    const urlSize = parseInt(searchParams.get('size')) || 16;
    
    // Update state if URL params are different
    if (urlCategory !== selectedCategory) {
      setSelectedCategory(urlCategory);
    }
    if (urlPage !== currentPage) {
      setCurrentPage(urlPage);
    }
    if (urlSize !== pageSize) {
      setPageSize(urlSize);
    }
    
    // Set highlighted main category based on selected category
    const mainCat = getMainCategoryForSubcategory(urlCategory);
    if (mainCat) {
      setHighlightedMainCategory(mainCat);
    } else {
      // If it's a main category itself
      const mainCategory = categoryHierarchy.find(cat => cat.key === urlCategory);
      if (mainCategory && !mainCategory.subcategories) {
        setHighlightedMainCategory(urlCategory);
      }
    }
    
    // Fetch data with current params
    fetchIssuersData(urlPage, urlSize, urlCategory);
    fetchCategoryStatistics();
  }, []); // Only run on mount
  
  // Save expanded states to sessionStorage
  useEffect(() => {
    if (Object.keys(expandedCategories).length > 0) {
      sessionStorage.setItem('bondCategories_expandedCategories', JSON.stringify(expandedCategories));
    }
  }, [expandedCategories]);
  
  useEffect(() => {
    if (Object.keys(expandedSubcategories).length > 0) {
      sessionStorage.setItem('bondCategories_expandedSubcategories', JSON.stringify(expandedSubcategories));
    }
  }, [expandedSubcategories]);
  
  // Restore expanded states from sessionStorage on mount
  useEffect(() => {
    const savedExpandedCategories = sessionStorage.getItem('bondCategories_expandedCategories');
    const savedExpandedSubcategories = sessionStorage.getItem('bondCategories_expandedSubcategories');
    
    if (savedExpandedCategories) {
      try {
        setExpandedCategories(JSON.parse(savedExpandedCategories));
      } catch (e) {
        console.error('Error parsing saved expanded categories:', e);
      }
    }
    
    if (savedExpandedSubcategories) {
      try {
        setExpandedSubcategories(JSON.parse(savedExpandedSubcategories));
      } catch (e) {
        console.error('Error parsing saved expanded subcategories:', e);
      }
    }
  }, []);

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the menu bar
      const menuBar = document.querySelector('.horizontal-menu-bar');
      if (menuBar && !menuBar.contains(event.target)) {
        setExpandedCategories({});
        setExpandedSubcategories({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);



  const handleViewDetails = (issuer) => {
    // Use corporateIdentityNumber as issuerId
    const issuerId = issuer.corporateIdentityNumber;
    // Navigate to dashboard route if we're in dashboard context, otherwise to landing page
    const isInDashboard = window.location.pathname.includes('/d/');
    if (isInDashboard) {
      navigate(`/d/issuer/details?issuerId=${issuerId}`);
    } else {
      navigate(`/issuer-details/${issuerId}`);
    }
  };



  const content = (
    <div className={`bond-categories-page ${isDark ? 'dark' : ''}`}>
      <div className="container">
        {/* Categories Section - Dropdown Cascading Menu Design */}
        <div className="categories-section">
          <Title level={1} className="categories-title">Issuer Categories</Title>
          
          <div className="horizontal-menu-bar">
            {categoryHierarchy.map((category) => (
              <div
                key={category.key}
                className={`menu-item menu-item-${category.key} ${isMainCategoryHighlighted(category.key) ? 'active' : ''}`}
                onClick={() => {
                  if (category.subcategories) {
                    handleMainCategoryToggle(category.key);
                  } else {
                    handleCategoryChange(category.key);
                  }
                }}
              >
                <div className="menu-item-icon">{category.icon}</div>
                <div className="menu-item-content">
                  <div className="menu-item-label">
                    {category.label}
                    {categoryCounts[category.key] !== undefined && (
                      <span className="menu-item-count-badge">
                        {categoryCounts[category.key] || 0}
                      </span>
                    )}
                  </div>
                </div>
                {category.subcategories && (
                  <div className="menu-item-dropdown-arrow">▼</div>
                )}
                
                {/* Dropdown Menu - Level 2 */}
                {category.subcategories && expandedCategories[category.key] && (
                  <div 
                    className="category-dropdown-menu" 
                    onClick={(e) => e.stopPropagation()}
                    onMouseLeave={() => setExpandedSubcategories({})}
                  >
                    {category.subcategories.map((subcategory) => (
                      <div
                        key={subcategory.key}
                        className={`dropdown-item ${selectedCategory === subcategory.key ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSubcategoryClick(subcategory.key, category.key, subcategory);
                        }}
                        onMouseEnter={() => handleSubcategoryHover(subcategory.key, subcategory.subcategories && subcategory.subcategories.length > 0)}
                      >
                        <div className="dropdown-item-icon">{subcategory.icon}</div>
                        <span className="dropdown-item-label">
                          {subcategory.label}
                        </span>
                        {categoryCounts[subcategory.key] !== undefined && (
                          <span className="dropdown-item-count">
                            {categoryCounts[subcategory.key] || 0}
                          </span>
                        )}
                        {subcategory.subcategories && subcategory.subcategories.length > 0 && (
                          <div className="dropdown-item-arrow">▶</div>
                        )}
                        
                        {/* Cascading Menu - Level 3 (appears on right) */}
                        {subcategory.subcategories && subcategory.subcategories.length > 0 && expandedSubcategories[subcategory.key] && (
                          <div 
                            className="cascading-menu"
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: '100%',
                              marginLeft: '8px'
                            }}
                          >
                            {subcategory.subcategories.map((level3Sub) => (
                              <div
                                key={level3Sub.key}
                                className={`cascading-item ${selectedCategory === level3Sub.key ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleLevel3SubcategoryClick(level3Sub.key, category.key);
                                }}
                              >
                                <div className="cascading-item-icon">{level3Sub.icon}</div>
                                <span className="cascading-item-label">
                                  {level3Sub.label}
                                </span>
                                {categoryCounts[level3Sub.key] !== undefined && (
                                  <span className="cascading-item-count">
                                    {categoryCounts[level3Sub.key] || 0}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Results section: Category Info Header Card */}
        <section className="results-section" aria-label="Results">
          <div className="category-info-card-header">
            <div className="category-info-card-header__info">
              {/* Breadcrumb path as tags */}
              <nav className="breadcrumb-nav" aria-label="Breadcrumb">
                {getBreadcrumbPath().map((item, i) => (
                  <span key={item.key || 'root'} className="breadcrumb-item">
                    {i > 0 && <RightOutlined className="breadcrumb-sep" aria-hidden />}
                    <span className={`breadcrumb-pill ${i === getBreadcrumbPath().length - 1 ? 'breadcrumb-current' : ''}`}>
                      {item.label}
                    </span>
                  </span>
                ))}
              </nav>

              {/* Dynamic Category Heading */}
              <h2 className="category-info-card-header__title">
                {getBreadcrumbPath()[getBreadcrumbPath().length - 1]?.label || 'Bond Categories'}
              </h2>
            </div>

            {/* Results count badges */}
            <div className="category-info-card-header__badges">
              <span className="summary-badge-category">
                {totalItems > 0 ? `${totalItems} ${totalItems === 1 ? 'Item' : 'Items'}` : 'No Items'}
              </span>
              {totalIssuersAcrossCategories() > 0 && (
                <span className="summary-badge-total">
                  {`${totalIssuersAcrossCategories().toLocaleString('en-IN')} Total`}
                </span>
              )}
            </div>
          </div>
        </section>

        <div className="issuers-grid">
          <Row gutter={[24, 24]}>
            {loading ? (
              // Loading state
              Array.from({ length: 8 }).map((_, index) => (
                <Col xs={24} sm={12} md={8} lg={6} key={index}>
                  <Card className="bond-card loading" hoverable style={{ borderRadius: '16px' }}>
                    <div className="bond-card-inner">
                      {/* Company Name and Category */}
                      <div className="issuer-header-section">
                        <div className="issuer-company-info">
                          <div className="company-logo">
                            <div className="company-logo-text loading-placeholder" style={{ width: '64px', height: '64px', backgroundColor: '#f0f0f0', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 'bold', color: '#999' }}>...</div>
                          </div>
                          <div className="issuer-text-info">
                            <div className="issuer-company-name">
                              <div className="loading-placeholder" style={{ width: '90%', height: '24px', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '8px' }}></div>
                            </div>
                            <div className="issuer-category">
                              <div className="loading-placeholder" style={{ width: '60%', height: '16px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}></div>
                            </div>
                          </div>
                        </div>
                        <div className="issuer-right-section">
                          <div className="loading-placeholder" style={{ width: '48px', height: '48px', backgroundColor: '#f0f0f0', borderRadius: '50%' }}></div>
                        </div>
                      </div>

                      {/* Bottom Section: Tags */}
                      <div className="issuer-bottom-section">
                        <div className="issuer-tags">
                          <div className="loading-placeholder" style={{ width: '80px', height: '24px', backgroundColor: '#f0f0f0', borderRadius: '12px', marginRight: '8px' }}></div>
                          <div className="loading-placeholder" style={{ width: '100px', height: '24px', backgroundColor: '#f0f0f0', borderRadius: '12px' }}></div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))
            ) : (
              // Real data from API
              issuersData.map((item, index) => {
                // Check if this is a Sovereign Securities or Sub-Sovereign Securities category
                if (selectedCategory === 'government' || selectedCategory === 'stategovernment') {
                  // Use ISINCard for government securities
                  return (
                    <Col xs={24} sm={12} md={8} lg={6} key={index}>
                      <ISINCard 
                        isin={item} 
                        index={index}
                        onViewDetails={handleViewDetails}
                      />
                    </Col>
                  );
                } else {
                  return (
                    <Col xs={24} sm={12} md={8} lg={6} key={index}>
                      <Card
                        className={`premium-issuer-card ${getRatingBorderClass(item.rating)}`}
                        hoverable
                        onClick={() => handleViewDetails(item)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="premium-issuer-card__body">
                          {/* Badges row */}
                          <div className="premium-issuer-card__badges-row">
                             <span className={`premium-issuer-card__rating-badge ${getRatingBadgeClass(item.rating)}`}>
                               {cleanRatingValue(item.rating)}
                             </span>
                            <span className={`premium-issuer-card__ownership-badge ${getOwnershipClass(item.typeofSubOwnership, item.typeOfIssuerBasedOnOwnership)}`}>
                              {formatSubOwnership(item.typeofSubOwnership || item.typeOfIssuerBasedOnOwnership)}
                            </span>
                          </div>

                          {/* Company Name */}
                          <h2 className="premium-issuer-card__title">
                            {item.issuer || ''}
                          </h2>

                          <div className="premium-issuer-card__divider" />

                          {/* Metrics Grid */}
                          <div className="premium-issuer-card__metrics-grid">
                            <div className="premium-issuer-card__metric-box">
                              <span className="premium-issuer-card__metric-label">Market Cap</span>
                              <span className="premium-issuer-card__metric-value">
                                {getMarketCapValue(item)}
                              </span>
                            </div>
                            <div className="premium-issuer-card__metric-box">
                              <span className="premium-issuer-card__metric-label">Bond Borrowings</span>
                              <span className="premium-issuer-card__metric-value">
                                {getBondBorrowingsValue(item)}
                              </span>
                            </div>
                          </div>

                          <div className="premium-issuer-card__divider" />

                          {/* Details Grid */}
                          <div className="premium-issuer-card__details-grid">
                            <div className="premium-issuer-card__detail-item">
                              <span className="premium-issuer-card__detail-label">Macro Sector</span>
                              <span className="premium-issuer-card__detail-value--neutral-badge">
                                {formatClassificationValue(item.classification?.macroeconomicSector || item.macroeconomicSector || '—')}
                              </span>
                            </div>
                            <div className="premium-issuer-card__detail-item">
                              <span className="premium-issuer-card__detail-label">Sector</span>
                              <span className="premium-issuer-card__detail-value--neutral-badge">
                                {formatClassificationValue(item.classification?.sector || item.sector || '—')}
                              </span>
                            </div>
                            <div className="premium-issuer-card__detail-item">
                              <span className="premium-issuer-card__detail-label">Industry</span>
                              <span className="premium-issuer-card__detail-value--neutral-badge">
                                {formatClassificationValue(item.classification?.industry || item.industry || '—')}
                              </span>
                            </div>
                            <div className="premium-issuer-card__detail-item">
                              <span className="premium-issuer-card__detail-label">Basic Industry</span>
                              <span className="premium-issuer-card__detail-value--neutral-badge">
                                {formatClassificationValue(item.classification?.basicIndustry || item.basicIndustry || '—')}
                              </span>
                            </div>
                          </div>

                        </div>

                        {/* Footer View Profile */}
                        <div className="premium-issuer-card__footer">
                          <span className="premium-issuer-card__footer-text">View Profile</span>
                          <RightOutlined className="premium-issuer-card__footer-arrow" />
                        </div>
                      </Card>
                    </Col>
                  );
                }
              })
            )}
          </Row>
        </div>


        {/* Pagination */}
        {totalItems > pageSize && (
          <div className="pagination-container">
            <Pagination
              current={currentPage}
              total={totalItems}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger
              onShowSizeChange={handlePageChange}
              showTotal={(total) => `Total ${total} items`}
              pageSizeOptions={['16', '32', '64', '128']}
            />
          </div>
        )}

        {/* Empty State */}
        {issuersData.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              <BankOutlined />
            </div>
            <Title level={3}>No issuers found</Title>
            <Paragraph>
              Try adjusting your search terms or browse all issuers
            </Paragraph>
            <Button
              type="primary"
              onClick={() => setSearchTerm('')}
              className="clear-search-btn"
            >
              Clear Search
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return skipLayout ? content : <LandingLayout>{content}</LandingLayout>;
};

export default IssuerCategoryRedesign;
